import Modal from "react-modal";
import React, {useEffect, useState} from "react";

import './Participant.clinicians.dialog.scss';
import {Subject} from "rxjs";
import {useLazyQuery, useMutation} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import {useTranslation} from "react-i18next";
import {ModalLog} from "../../services/interactions/elements/modal";
import AuditTrail, {PAGES} from "../../services/interactions/audit.trail";

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: "none",
        padding: "0px",
        boxShadow: "0 3px 8px 0 rgba(0, 0, 0, 0.38)",
    }
};
Modal.setAppElement('#root');

function TeamMemberItem({member, onSelected}) {

    const handleClick = () => {
        onSelected(member);
    }

    return (
        <div className={`MemberItem`} onClick={handleClick}>
                <p className="ellipsis">
                    {member.name}
                </p>
                <p className="ellipsis">
                    {member.email}
                </p>
                <p className="capitalize">
                    {member.role}
                </p>
                <p>
                    {member.type}
                </p>
            </div>
    );
}

const _Modal = ModalLog(Modal);

function ParticipantCliniciansDialog({program, item, items, openModal, closeModal}) {
    const {t} = useTranslation(['common', 'teams']);
    const [members, setMembers] = useState([]);
    const [membersSelected, setMembersSelected] = useState([]);

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');
    const [searchTerm] = useState(new Subject());

    const [__submitting, __setSubmitting] = useState(false);

    const [getProgram] = useLazyQuery(gqlSchemaLibrary.programs.query.getUsersAndCliniciansByProgram, {
        onCompleted: (data) => {
            const _members = members;
            for (const t of data.getProgramById.teams) {
                const d = {
                    _id: t._id,
                    name: t.name,
                    role: "",
                    email: "",
                    type: "Team"
                };
                _members.push(d);
            }
            for (const m of data.getProgramById.users) {
                const name = m.user.bio.lastname + ', ' + m.user.bio.firstname;
                const d = {
                    _id: m._id,
                    name: name,
                    role: m.role,
                    email: m.user.email,
                    type: "User"
                };
                _members.push(d);
            }
            setMembers(Object.assign([], _members));
        },
        fetchPolicy: 'cache-and-network'
    })

    const [getUsersAndTeams] = useLazyQuery(gqlSchemaLibrary.patients.getUserAndTeamsByPatient, {
        onCompleted: (data) => {
            const _members = [];
            for (const m of data.getUserAndTeamsByPatient.clinicians) {
                const name = m.user.bio.lastname + ', ' + m.user.bio.firstname;
                _members.push({
                    _id: m._id,
                    name: name,
                    role: m.role,
                    email: m.user.email,
                    type: "User"
                });
            }
            for (const t of data.getUserAndTeamsByPatient.teams) {
                _members.push({
                    _id: t._id,
                    name: t.name,
                    role: "",
                    email: "",
                    type: "Team"
                });
            }
            setMembersSelected(_members);
        },
        fetchPolicy: 'cache-and-network'
    })

    const [editUsersTeams] = useMutation(gqlSchemaLibrary.patients.mutations.assignUsersTeams, {
        onCompleted: ({ data }) => {
            __setSubmitting(false);
            closeModal(item? [item] : items, membersSelected.map((p) => p._id));
        },
        onError: (e) => {
            console.error('Rename Team Error: ', e);
            __setSubmitting(false);
        }
    });

    useEffect(() => {
        setMembersSelected([]);
        setMembers([]);
        changeSearch('')
        searchTerm.next('')
        if (openModal && (item || items.length)) {
            getProgram({
                variables : {
                    id: program._id
                }
            });
            if (item) {
                getUsersAndTeams({
                    variables: {
                        id: item._id
                    }
                });
            }
        }
    }, [item, openModal, getProgram])

    const closeModalEmpty = () => {
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'button', event: 'closeModal', action: 'button close modal', program_id: program._id});
        closeModal();
    }

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'input', event: 'onSearch', action: 'input modal search users/teams', focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const onSelected = (program) => {
        const index = membersSelected.indexOf(program);
        if (index === -1) {
            AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'row', event: 'onClick', action: 'modal row add item', program_id: program._id});
            membersSelected.push(program);
        }
        setMembersSelected(Object.assign([], membersSelected));
    }

    const filteredMembers = members.filter(t => {
        if (membersSelected.find((m) => m._id === t._id)) {
            return false;
        }
        if (!search) {
            return true;
        }
        const name = (t.name || '').toLowerCase();
        return name.includes(search.toLowerCase());
    }).sort((a, b) => {
        return a.name.localeCompare(b.name);
    })

    const onDeleteItem = (member) => {
        const index = membersSelected.indexOf(member);
        if (index > -1) {
            AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'row', event: 'onClick', action: 'modal row delete item', program_id: program._id});
            membersSelected.splice(index, 1);
        }
        setMembersSelected(Object.assign([], membersSelected));
    }

    const onSave = () => {
        if (__submitting) {
            return;
        }
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'button', event: 'onClick', action: 'modal save assign/unassign participant(s)', program_id: program._id});
        __setSubmitting(true);
        const users = membersSelected.filter((m) => m.type === 'User').map((u) => u._id);
        const teams = membersSelected.filter((m) => m.type === 'Team').map((u) => u._id);
        editUsersTeams({
            variables: {
                participant: {
                    participantsIds: item? [item._id] : items.map((u) => u._id),
                    users: users,
                    teams: teams
                }
            }
        });
    }

    return (
        <_Modal
        isOpen={!!openModal}
        style={customStyles}
        onRequestClose={closeModalEmpty}
        contentLabel="Add users to participants"
        label="Add users to participants"
        event={PAGES.PROGRAM_PARTICIPANTS}>
            <div id="ParticipantsMembersDialog">
                <div className="__MembersTitle">
                    <h3>{t('teams:assign-unassign-to-participant')}</h3>
                </div>
                <div className="__Container">
                    <p className="__MembersSubTitle">
                        <b>{t('teams:assign-users-teams')}</b>
                    </p>
                    <p>
                        {t('teams:click-on-the-name-assign')}
                    </p>
                    <div className="InputComponent">
                        <input value={search} placeholder="Find a user or team" required={false} onChange={handleSearch}
                           onFocus={handleFocus(true)} onBlur={handleFocus(false)}/>
                    </div>
                    <div className="__MembersList">
                        <div className="headers">
                            <div className="selectable">
                                <p>
                                    {t('name')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('email')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('role')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('type')}
                                </p>
                            </div>
                        </div>
                        {
                            filteredMembers.map((member, index) => (
                                <TeamMemberItem key={index} member={member} onSelected={onSelected} />
                            ))
                        }
                    </div>
                    <p className="__MembersSubTitle borderTop">
                        <b>{t('teams:unassign-users-teams')}</b>
                    </p>
                    <p>
                        {t('teams:click-on-the-name-unassign')}
                    </p>
                    <div className="tags">
                        {
                            membersSelected.map((member, index) => (
                                <span key={'s-'+index} className="selectedMember">
                                    {member.name}
                                    <i className="material-icons" onClick={() => onDeleteItem(member)}>close</i>
                                </span>
                            ))
                        }
                    </div>
                    <div className="containerButtons">
                        <button className="primary gray" onClick={closeModalEmpty}>
                            {t('cancel')}
                        </button>
                        <button className="primary green" onClick={onSave}>
                            {t('save')}
                        </button>
                    </div>
                </div>
            </div>
        </_Modal>
    );
}

export default ParticipantCliniciansDialog;
